import { TextBoxSelectionWithPriceAndName } from "./textBoxSelectionWithPriceAndName";
import { TextBoxSelectionWithPrice } from "./textBoxSelectionWithPrice";
import { TextBoxSelectionWithName } from "./textBoxSelectionWithName";
import TextBoxSelectionComponent from "./textBoxSelectionList";
import { useSnack } from "hooks/useSnack";

export interface TextBoxSelectionProps {
  textBoxSelectionType?: TextBoxSelectionType;
  label?: string;
  checked: boolean;
  onSelectionChange: (checked: boolean) => void;
  price?: number;
  onPriceChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  onNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeHolder?: string;
}

export enum TextBoxSelectionType {
  selection = "selection",
  name = "name",
  price = "price",
  nameAndPrice = "nameAndPrice",
}

export interface TextBoxSelectionData {
  id: number;
  optionName: string;
  isChecked: boolean;
  type: TextBoxSelectionType;
  price?: number;
  enteredName?: string;
  duplicates?: boolean;
  isTemplate?: boolean;
  placeholder?: string;
}

export const TextBoxSelection: React.FC<TextBoxSelectionProps> = ({
  textBoxSelectionType = TextBoxSelectionType.selection,
  label = "",
  checked,
  onSelectionChange,
  onPriceChange = () => {},
  disabled = false,
  price = 0,
  name = "",
  onNameChange = () => {},
  placeHolder = "Enter Value",
}) => {
  const showSnack = useSnack();

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Remove leading zeros but preserve valid decimals like "0.5"
    const normalized = /^0\.\d*$/.test(raw)
      ? raw
      : raw.replace(/^0+(\d)/, "$1");
    onPriceChange({
      ...e,
      target: { ...e.target, value: normalized },
    });
  };

  if (textBoxSelectionType === TextBoxSelectionType.nameAndPrice) {
    return (
      <TextBoxSelectionWithPriceAndName
        label={label}
        checked={checked}
        onSelectionChange={(checked) => onSelectionChange(checked)}
        onPriceChange={(e) => handlePriceChange(e)}
        onNameChange={(e) => onNameChange(e)}
        disabled={disabled}
        name={name}
        price={price}
        placeHolder={placeHolder}
      />
    );
  } else if (textBoxSelectionType === TextBoxSelectionType.price) {
    return (
      <TextBoxSelectionWithPrice
        label={label}
        checked={checked}
        onSelectionChange={(checked) => onSelectionChange(checked)}
        onPriceChange={(e) => handlePriceChange(e)}
        disabled={disabled}
        price={price}
      />
    );
  } else if (textBoxSelectionType === TextBoxSelectionType.name) {
    return (
      <TextBoxSelectionWithName
        label={label}
        checked={checked}
        onSelectionChange={(checked) => onSelectionChange(checked)}
        onNameChange={(e) => onNameChange(e)}
        disabled={disabled}
        name={name}
        placeHolder={placeHolder}
      />
    );
  } else if (textBoxSelectionType === TextBoxSelectionType.selection) {
    return (
      <TextBoxSelectionComponent
        label={label}
        checked={checked}
        onChange={(checked) => onSelectionChange(checked)}
      />
    );
  } else {
    showSnack("Invalid Input", "error");
    return null;
  }
};

export const handlePriceChange = (
  setData: React.Dispatch<React.SetStateAction<any>>,
  id: number,
  e: React.ChangeEvent<HTMLInputElement>,
  optionType: string,
) => {
  const newPrice = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;
  updateItemInArray<TextBoxSelectionData, "price">(
    setData,
    optionType,
    id,
    "price",
    newPrice,
  );
};

export const handleNameChange = (
  setData: React.Dispatch<React.SetStateAction<any>>,
  id: number,
  e: React.ChangeEvent<HTMLInputElement>,
  optionType: string,
) => {
  updateItemInArray<TextBoxSelectionData, "enteredName">(
    setData,
    optionType,
    id,
    "enteredName",
    e.target.value,
  );
};

/* Helper function to handle selection change and duplicate item if needed */
export function handleSelectionChange<
  T extends { id: number; optionName: string; isChecked: boolean },
>(
  setData: React.Dispatch<React.SetStateAction<any>>,
  value: T[keyof T],
  key: string,
  id: number,
  duplicates = false,
) {
  if (duplicates) {
    setData((prev: any) => {
      const items = prev[key] as any[];
      const item = items.find((i) => i.id === id);
      if (!item) return prev;

      if (item.isTemplate) {
        if (!value) return prev; // template can never be unchecked
        // Insert a new checked clone directly above the template
        const idx = items.findIndex((i) => i.id === id);
        const maxId = Math.max(...items.map((i) => i.id), 0);
        const clone = {
          ...item,
          id: maxId + 1,
          isChecked: true,
          isTemplate: false,
        };
        return {
          ...prev,
          [key]: [...items.slice(0, idx), clone, ...items.slice(idx)],
        };
      } else {
        if (!value) {
          // Unchecking a clone removes it
          return { ...prev, [key]: items.filter((i) => i.id !== id) };
        }
        // Re-checking a clone just marks it checked
        return {
          ...prev,
          [key]: items.map((i) =>
            i.id === id ? { ...i, isChecked: true } : i,
          ),
        };
      }
    });
    return;
  }

  // Normal items: just toggle isChecked
  setData((prev: any) => ({
    ...prev,
    [key]: prev[key].map((item: any) =>
      item.id === id ? { ...item, isChecked: value } : item,
    ),
  }));
}

function updateItemInArray<T extends { id: number }, K extends keyof T>(
  setData: React.Dispatch<React.SetStateAction<any>>, // your state setter
  optionType: string,
  id: number,
  key: K,
  value: T[K],
) {
  setData((prevData: any) => ({
    ...prevData,
    [optionType]: prevData[optionType].map((item: T) =>
      item.id === id ? { ...item, [key]: value } : item,
    ),
  }));
}
